/**
 * Bridge between the React-side Logto SDK and the module-level Apollo
 * authLink.
 *
 * The Apollo client is created in `index.js`, outside the React tree,
 * so it cannot call the `useLogto()` hook directly. Instead, a small
 * React component inside the tree (see `LogtoAccessTokenRegistrar`)
 * registers the SDK's `getAccessToken` here on mount, and the auth
 * link reads through this module on each request.
 *
 * Mirrors the same pattern used in admin-ui.
 */

import { useLogto } from "@logto/react";
import { useEffect } from "react";
import { logtoApiResource } from "../config/logto";

let provider;

export const setAccessTokenProvider = (fn) => {
  provider = fn;
};

export const getAccessTokenForApollo = async () => {
  if (!provider) return undefined;
  try {
    return await provider(logtoApiResource());
  } catch {
    return undefined;
  }
};

/**
 * Bridge for an auth-failure handler. Auth-context registers a function
 * here at mount that knows how to drop the Logto session and bounce the
 * user back through sign-in. The Apollo errorLink — which lives outside
 * the React tree — calls this when a session has actually ended, so a
 * stale Logto cache can't wedge the UI on the spinner.
 *
 * The `terminal` flag distinguishes "the refresh cookie was rejected"
 * (true: tear the Logto SDK session down too, the user must
 * re-authenticate) from "we ran out of retries on a transport failure"
 * (false: clear locally and let them sign in again without a full
 * end_session round trip). Defaults to true so an un-annotated caller
 * gets the old, more aggressive behaviour rather than a half-cleared
 * session.
 */
let authFailureHandler;

export const setAuthFailureHandler = (fn) => {
  authFailureHandler = fn;
};

export const invokeAuthFailureHandler = ({ terminal = true } = {}) => {
  if (typeof authFailureHandler === "function") {
    authFailureHandler({ terminal });
  }
};

/**
 * Mount this once at the top of the React tree (inside LogtoProvider).
 * It registers the SDK's getAccessToken with the bridge so the Apollo
 * authLink can read it on each outgoing request.
 *
 * The registration is gated on `isAuthenticated` (and `!isLoading`)
 * for a reason: Logto's SDK memoises the in-flight getAccessToken
 * promise. If something calls `getAccessToken` before sign-in is
 * complete (e.g. AuthProvider firing a query during the callback
 * page), the SDK rejects with `LogtoClientError: Not authenticated`
 * and *caches* that rejection — so the subsequent useHandleSignInCallback
 * also surfaces "Not authenticated" rather than completing. Withholding
 * the provider until the session is good keeps the memo clean.
 */
export const LogtoAccessTokenRegistrar = () => {
  const { getAccessToken, isAuthenticated, isLoading } = useLogto();
  useEffect(() => {
    if (isLoading || !isAuthenticated) {
      setAccessTokenProvider(undefined);
      return;
    }
    setAccessTokenProvider((resource) => getAccessToken(resource));
    return () => setAccessTokenProvider(undefined);
  }, [getAccessToken, isAuthenticated, isLoading]);
  return null;
};

export default LogtoAccessTokenRegistrar;
